import { Info } from "lucide-react";
import type { FC, ReactNode } from "react";

import { ListCard, ListRow } from "@/components/ui/list-card";
import { SectionHeader } from "@/components/ui/page-header";

export const NEEDS_BACKEND_COPY = "Saving this needs the latest backend";

/**
 * A firm record without the setting's key comes from an older backend that
 * would drop it on save; the control says so and keeps Save off.
 */
export const NeedsBackendNotice: FC<{ testId: string }> = ({ testId }) => (
  <span
    className="flex items-center gap-1.5 text-sm text-fg-3"
    data-testid={testId}
  >
    <Info className="size-3.5 shrink-0" aria-hidden />
    {NEEDS_BACKEND_COPY}
  </span>
);

type SectionProps = {
  title: string;
  description: ReactNode;
  testId: string;
  children: ReactNode;
};

/** A page section: its header 12px above one card, the settings rows split by dividers. */
export const CasePrepSection: FC<SectionProps> = ({
  title,
  description,
  testId,
  children
}) => (
  <section className="flex w-full flex-col gap-3" data-testid={testId}>
    <SectionHeader title={title} description={description} />
    <ListCard>{children}</ListCard>
  </section>
);

type RowProps = {
  title: ReactNode;
  description?: ReactNode;
  /** Right-aligned: a select, a picker or buttons. */
  control?: ReactNode;
  /** Small buttons right under the control, like Reset. */
  actions?: ReactNode;
  testId?: string;
  /** Full-width content under the label: a text box, a list, a notice. */
  children?: ReactNode;
};

/** One setting in a section's card: label and helper left, control right. */
export const CasePrepRow: FC<RowProps> = ({
  title,
  description,
  control,
  actions,
  testId,
  children
}) => (
  <div className="flex w-full min-w-0 flex-col" data-testid={testId}>
    <ListRow
      title={title}
      description={
        description ? (
          <span className="block text-sm text-fg-3">{description}</span>
        ) : undefined
      }
      control={
        actions ? (
          <div className="flex flex-col items-end gap-1">
            {control}
            <div className="flex items-center gap-1">{actions}</div>
          </div>
        ) : (
          control
        )
      }
    />
    {children ? (
      <div className="flex min-w-0 flex-col gap-2 px-4 pb-3">{children}</div>
    ) : null}
  </div>
);
